<template>
    <div id="manage-attendance-request-page">
        <!-- HEADER -->
        <div class="action-header">
            <div class="search-container">
                <div class="search-input">
                    <v-text-field
                        v-model="search"
                        prepend-inner-icon="search"
                        :placeholder="this.$vuetify.lang.t('$vuetify.manageAttendanceRequest.searchByEmployeeName')"
                        solo
                        height="40px"
                        @keyup.enter="getAttendancesRequestPagination()">
                    </v-text-field>
                </div>

                <div class="ml-3">
                    <v-btn
                        @click="searchAttendancesRequest()"
                        height="40px"
                        color="primary"
                        outlined>
                        {{ $vuetify.lang.t('$vuetify.manageAttendanceRequest.search') }}
                    </v-btn>
                </div>

                <div class="ml-day-inquiry">
                    <v-menu
                        ref="inquiryDayMenu"
                        v-model="datePickerShow"
                        :close-on-content-click="false"
                        transition="scale-transition"
                        offset-y
                        min-width="234px">
                        <template v-slot:activator="{ on }">
                            <v-text-field
                                prepend-inner-icon="event"
                                @click:prepend-inner="datePickerShow = true"
                                v-model="dateFormatted"
                                readonly
                                v-on="on"
                                outlined
                                height="40px"
                                background-color="#ffffff"
                            ></v-text-field>
                        </template>
                        <v-date-picker
                            :locale="localeLang"
                            ref="inquiryDayPicker"
                            v-model="inquiryDay"
                            @change="getInquiryByDay"
                        ></v-date-picker>
                    </v-menu>
                </div>
            </div>
        </div>
        <!-- END HEADER -->

        <!-- DATATABLE -->
        <v-card class="content-container">
            <v-data-table
                :footer-props="{
                    'items-per-page-options': [10, 20, 30, 50, 100]
                }"
                @page-count="pageCount = $event"
                :page.sync="page"
                :headers="headers"
                :options.sync="options"
                :server-items-length="totalData"
                :items="items"
                :loading="loading">
                <template v-slot:[`item.status`]="{ item }">
                    <div :class="statusLabel(item.status['key'])">
                        <div>{{ item.status['value'] === 'Present' ? 'Ontime' : item.status['value'] }}</div>
                    </div>
                </template>
                <template v-slot:[`item.action`]="{ item }">
                    <v-icon
                        v-if="permissionUpdate"
                        class="mr-2"
                        @click="updateInquiryStatus(item.id)">
                        edit
                    </v-icon>
                </template>
            </v-data-table>
        </v-card>
        <!-- END DATATABLE -->

        <!-- PAGINATION -->
        <div class="pt-2 paginator-box">
            <div>
                <v-pagination v-model="page" :length="pageCount"></v-pagination>
            </div>
        </div>
        <!-- END PAGINATION -->
    </div>
</template>

<script>
import permissionConstant from '@/constants/permissions.js'
export default {
    name: 'ManageAttendanceRequest',

    data () {
        return {
            // Filter
            search: '',
            inquiryDay: this.$util.getCurrentDate(),
            datePickerShow: false,
            dateFormatted: this.$util.formatDate(this.$util.getCurrentDate()),
            pageCount: 1,
            page: 1,
            options: {},
            items: [],
            totalData: 0,
            loading: false,
            branch: null,
            permissionUpdate: true
        }
    },

    computed: {
        localeLang () {
            return this.$vuetify.lang.current
        },
        baseChoiceClass () {
            if (this.loading) {
                return 'choice disable'
            }
            return 'choice'
        },
        headers () {
            return [
                { text: this.$vuetify.lang.t('$vuetify.manageAttendanceRequest.employeeNo'), value: 'employee_no', width: '10%' },
                { text: this.$vuetify.lang.t('$vuetify.manageAttendanceRequest.name'), value: 'fullname', width: '20%' },
                { text: this.$vuetify.lang.t('$vuetify.manageAttendanceRequest.submitted_date'), value: 'submitted_date', width: '10%' },
                { text: this.$vuetify.lang.t('$vuetify.manageAttendanceRequest.group_name'), value: 'group_name', width: '15%' },
                { text: this.$vuetify.lang.t('$vuetify.manageAttendanceRequest.department_name'), value: 'department_name', width: '15%' },
                { text: this.$vuetify.lang.t('$vuetify.manageAttendanceRequest.request_date'), value: 'request_date', width: '10%' },
                { text: this.$vuetify.lang.t('$vuetify.manageAttendanceRequest.status'), value: 'status', width: '14%' },
                { text: '', value: 'action', width: '12%', align: 'center', sortable: false }
            ]
        }
    },

    methods: {
        async searchAttendancesRequest () {
            this.options.page = 1
            // console.log('Searching')
            await this.getAttendancesRequestPagination()
        },
        async getAttendancesRequestPagination () {
            this.loading = true
            const { sortBy, sortDesc, page, itemsPerPage } = this.options

            let url = `attendance-request?status=&branch=${this.branch}&inquiryDay=${this.inquiryDay}&page=${page}&itemsPerPage=${itemsPerPage}`

            if (sortBy[0]) {
                url += `&sortBy=${sortBy[0]}`
            }

            if (sortDesc[0]) {
                url += `&sortDesc=${sortDesc[0]}`
            }

            if (this.search) {
                url += `&search=${this.search}`
            }

            const res = await this.axios.get(url)
            if (res && res.data && res.data.status === 'ok') {
                this.items = res.data.data
                this.totalData = res.data.total
                this.loading = false
            }
        },

        async getInquiryByDay (date) {
            this.$refs.inquiryDayMenu.save(date)
            this.dateFormatted = this.$util.formatDate(date)
            await this.getAttendancesRequestPagination()
        },

        async getBranch () {
            this.branch = localStorage.getItem('currentBranchId')
            if (this.branch) {
                await this.getAttendancesRequestPagination()
            }
        },

        choiceClass (status, choice) {
            if (status === choice) {
                return `${this.baseChoiceClass} active`
            }

            return this.baseChoiceClass
        },

        statusLabel (status) {
            let style = ''
            if (status === 'PND') {
                style += 'highlight'
            } else if (status === 'APV') {
                style += 'success'
            }

            return style
        },
        updateInquiryStatus (id) {
            const dateSelect = this.inquiryDay
            this.$router.push({ name: 'AttendanceForm', params: { id, dateSelect } }, () => {})
        },
        checkPermission () {
            this.permissionUpdate = this.$util.checkPermission(permissionConstant.attendance.update)
        }
    },

    async mounted () {
        await this.getBranch()
        const { dateSelect } = this.$route.params
        if (dateSelect) {
            this.inquiryDay = new Date(dateSelect).toISOString().substr(0, 10)
            this.dateFormatted = this.$util.formatDate(new Date(dateSelect).toISOString().substr(0, 10))
        }
        this.checkPermission()
    },

    watch: {
        options: {
            async handler () {
                if (this.branch) {
                    await this.getAttendancesRequestPagination()
                }
            }
        },
        '$vuetify.lang.current': {
            handler: async function (after, before) {
                await this.getAttendancesRequestPagination()
            },
            deep: true
        }
    }
}
</script>

<style lang="scss" scoped>
::v-deep .v-input__control > .v-input__slot {
    min-height: auto !important;
    display: flex !important;
    align-items: center !important;
}

::v-deep .search-input .v-input__control > .v-input__slot {
    box-shadow: none !important;
    border: 1px solid #a8a8a8 !important;
}

::v-deep .v-input__prepend-inner {
    margin: auto !important;
}

.ml-day-inquiry {
    margin-left: 40px;
}

.highlight {
    font-size: 14px;
    width: fit-content;
    padding: 0rem 1rem;
    background-color: #ff544a;
    border-radius: 25px;
    color: white;
}
.success {
    font-size: 14px;
    width: fit-content;
    padding: 0rem 1rem;
    background-color: #5cb85c;
    border-radius: 25px;
    color: white;
}
</style>
